import { useTranslation } from 'next-i18next';
import { TreatmentScheduleContext } from 'pages/les-outils/planning-de-traitement';
import React, { ComponentPropsWithRef, FC, useContext, useEffect, useRef } from 'react';
import { ForecastQueries, Range } from '@api/business/treatmentHook/useForecastTool';
import { FlatButtonVariant } from '@components/buttons/Buttons.type';
import { Text } from '@components/Text/Text.component';
import { formValidator } from '@form/business/form.types';
import { FormValidators, useFormState } from '@form/business/useFormState.hook';
import { PlcForm } from '@form/components/PlcForm/PlcForm.component';
import { PLCSlider } from '@form/components/PLCSlider/PLCSlider.component';
import { PlcButtonGroup } from '@layout/components/PlcButtonGroup/PlcButtonGroup.component';
import { useLocation } from '@meteo/business/LocationSelector.hook';
import { MeteoCityAutocomplete } from '@meteo/components/MeteoCityAutocomplete/MeteoCityAutocomplete.component';
import { City } from '@meteo/types';
import classnames from 'classnames';
import style from './CriteriaForm.module.scss';

type Props = ComponentPropsWithRef<'form'> & {
  forecastQueries: ForecastQueries;
  onForecastQueryChange: (query: keyof ForecastQueries, min: number, max: number) => void;
  getPrevision: (() => void) | undefined;
};

type MeteoCityAutocompleteState = {
  city?: City;
};

const TEMP_RANGE_MIN = { max: 20, min: 0 };
const TEMP_RANGE_MAX = { max: 40, min: 10 };
const RELATIVE_HUMIDITY_RANGE_MIN = { max: 70, min: 40 };
const RELATIVE_HUMIDITY_RANGE_MAX = { max: 100, min: 60 };
const WIND_SPEED_RANGE_MIN = { max: 1, min: 0 };
const WIND_SPEED_RANGE_MAX = { max: 19, min: 1 };
const PRECIPITATION_AMOUNT_RANGE_MIN = { max: 0.1, min: 0 };
const PRECIPITATION_AMOUNT_RANGE_MAX = { max: 10, min: 0.1 };

function handleChangeValidation(minValue: number, maxValue: number, minRange: Range, maxRange: Range): boolean {
  return (
    minValue < maxValue &&
    minValue >= minRange.min &&
    minValue <= minRange.max &&
    maxValue >= maxRange.min &&
    maxValue <= maxRange.max
  );
}

const CriteriaForm: FC<Props> = ({ forecastQueries, onForecastQueryChange, getPrevision, className, ...rest }) => {
  const { t } = useTranslation(['common', 'meteo']);
  const rootClass = classnames(style['criteria-form'], className);
  const {
    city: { data: selectedCity, setData: setSelectedCity },
  } = useContext(TreatmentScheduleContext);
  const { geolocationError, city } = useLocation(selectedCity);

  const formRef = useRef<HTMLFormElement>(null);
  const cityRef = useRef<HTMLDivElement>(null);

  const initialFormState: MeteoCityAutocompleteState = {
    city,
  };

  const validators: FormValidators<MeteoCityAutocompleteState> = {
    city: {
      method: (value) => [
        {
          isValid: !geolocationError,
          message: geolocationError || t('meteo.error.geolocation'),
        },
        formValidator(value, t, true).required(),
      ],
      ref: cityRef,
    },
  };

  const { plcFormProps, setFormField, ErrorMessageBasedOnCode, isSubmitted } = useFormState({
    initialFormState,
    useNavigationWarn: true,
    validators,
  });

  function handleOnSubmit(event: React.FormEvent<HTMLFormElement> | undefined) {
    if (event) {
      event.preventDefault();
    }
    if (getPrevision) {
      getPrevision();
    }
  }

  useEffect(() => {
    setFormField('city', city);
  }, [city]);

  return (
    <PlcForm
      className={rootClass}
      onSubmitCallback={async (event) => handleOnSubmit(event)}
      ref={formRef}
      {...plcFormProps}
      {...rest}
    >
      <MeteoCityAutocomplete
        className="plc-mb-xl"
        isHintVisible={isSubmitted || Boolean(geolocationError)}
        onSelect={(c) => (c && setSelectedCity ? setSelectedCity(c) : undefined)}
        ref={cityRef}
        validators={validators.city?.method(city)}
      />
      <div className={style['criteria-form-container']}>
        <div className={style.column}>
          <div className="plc-mb-l">
            <Text
              className="plc-mb-m"
              i18nKey="meteo.metric.precipitationAmountInMillimeter.label"
              tag="div"
              variant="h6"
            />
            <PLCSlider
              aria-labelledby="range-slider"
              marks={[
                {
                  label: PRECIPITATION_AMOUNT_RANGE_MIN.min,
                  value: PRECIPITATION_AMOUNT_RANGE_MIN.min,
                },
                {
                  label: PRECIPITATION_AMOUNT_RANGE_MAX.max,
                  value: PRECIPITATION_AMOUNT_RANGE_MAX.max,
                },
              ]}
              max={PRECIPITATION_AMOUNT_RANGE_MAX.max}
              min={PRECIPITATION_AMOUNT_RANGE_MIN.min}
              onChange={(e, value) => {
                const max = value as number;
                const { min } = PRECIPITATION_AMOUNT_RANGE_MIN;
                e.preventDefault();
                if (handleChangeValidation(min, max, PRECIPITATION_AMOUNT_RANGE_MIN, PRECIPITATION_AMOUNT_RANGE_MAX)) {
                  onForecastQueryChange('precipitationAmountRange', min, max);
                }
              }}
              step={0.1}
              value={forecastQueries.precipitationAmountRange.max}
              valueLabelDisplay="auto"
            />
          </div>
          <div className="plc-mb-l">
            <Text className="plc-mb-m" i18nKey="meteo.metric.relativeHumidityInPercent.label" tag="div" variant="h6" />
            <PLCSlider
              aria-labelledby="range-slider"
              marks={[
                {
                  label: RELATIVE_HUMIDITY_RANGE_MIN.min,
                  value: RELATIVE_HUMIDITY_RANGE_MIN.min,
                },
                {
                  label: RELATIVE_HUMIDITY_RANGE_MAX.max,
                  value: RELATIVE_HUMIDITY_RANGE_MAX.max,
                },
              ]}
              max={RELATIVE_HUMIDITY_RANGE_MAX.max}
              min={RELATIVE_HUMIDITY_RANGE_MIN.min}
              onChange={(e, values) => {
                const [min, max] = values as Array<number>;
                e.preventDefault();
                if (handleChangeValidation(min, max, RELATIVE_HUMIDITY_RANGE_MIN, RELATIVE_HUMIDITY_RANGE_MAX)) {
                  onForecastQueryChange('relativeHumidityRange', min, max);
                }
              }}
              step={5}
              value={[forecastQueries.relativeHumidityRange.min, forecastQueries.relativeHumidityRange.max]}
              valueLabelDisplay="auto"
            />
          </div>
        </div>

        <div className={style.column}>
          <div className="plc-mb-l">
            <Text className="plc-mb-m" i18nKey="meteo.metric.min-max-temperature" tag="div" variant="h6" />
            <PLCSlider
              aria-labelledby="range-slider"
              marks={[
                {
                  label: TEMP_RANGE_MIN.min,
                  value: TEMP_RANGE_MIN.min,
                },
                {
                  label: TEMP_RANGE_MAX.max,
                  value: TEMP_RANGE_MAX.max,
                },
              ]}
              max={TEMP_RANGE_MAX.max}
              min={TEMP_RANGE_MIN.min}
              onChange={(e, values) => {
                const [min, max] = values as Array<number>;
                e.preventDefault();
                if (handleChangeValidation(min, max, TEMP_RANGE_MIN, TEMP_RANGE_MAX)) {
                  onForecastQueryChange('temperatureRange', min, max);
                }
              }}
              step={1}
              value={[forecastQueries.temperatureRange.min, forecastQueries.temperatureRange.max]}
              valueLabelDisplay="auto"
            />
          </div>
          <div className="plc-mb-l">
            <Text className="plc-mb-m" i18nKey="meteo.metric.wind-speed" tag="div" variant="h6" />
            <PLCSlider
              aria-labelledby="range-slider"
              marks={[
                {
                  label: WIND_SPEED_RANGE_MIN.min,
                  value: WIND_SPEED_RANGE_MIN.min,
                },
                {
                  label: WIND_SPEED_RANGE_MAX.max,
                  value: WIND_SPEED_RANGE_MAX.max,
                },
              ]}
              max={WIND_SPEED_RANGE_MAX.max}
              min={WIND_SPEED_RANGE_MIN.min}
              onChange={(e, value) => {
                const max = value as number;
                const { min } = WIND_SPEED_RANGE_MIN;
                e.preventDefault();
                if (handleChangeValidation(min, max, WIND_SPEED_RANGE_MIN, WIND_SPEED_RANGE_MAX)) {
                  onForecastQueryChange('windSpeedRange', min, max);
                }
              }}
              step={1}
              value={forecastQueries.windSpeedRange.max}
              valueLabelDisplay="auto"
            />
          </div>
        </div>
      </div>
      <PlcButtonGroup
        buttons={[
          {
            textI18nKey: 'common.cta.label.update',
            type: 'submit',
            variant: FlatButtonVariant.primary,
          },
        ]}
      />
      <ErrorMessageBasedOnCode className="plc-mt-m" />
    </PlcForm>
  );
};

export { CriteriaForm };
